import { List, Map } from 'immutable';

import { isValidNumber, toFixed, toString } from './number';
import { getInventoryOperationValue } from './product/getOperationValue';
import { shapeVariationsForApp } from './variations/shapeForApp';
import { shapeVariationsForAPI } from './variations/shapeForAPI';
import { reduceErrors } from './errors';
import { makeValue } from './product/shapeAttributes';
import { getSize } from './iterable/getSize';
import { toJS } from './iterable/toJS';
import { get } from './iterable/get';

import { AMOUNT, DEFAULT_OPTION, DEFAULT_VARIANT, ERROR, OVERSET, VALUE, WEIGHT_UNITS } from '../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../constants/channels';
import { INVENTORY_OPERATIONS } from '../constants/bulkEdit';
import { ATTRIBUTE } from '../constants/attributes';
import { FORM } from '../constants/forms';

function getOperationType({ channel, type }) {
  try {
    return INVENTORY_OPERATIONS[channel][type];
  } catch (error) {
    return undefined;
  }
}

export function makeOperation({ channel, productId, type, value }) {
  const operationType = getOperationType({ channel, type });

  if (!operationType || !value) return undefined;

  const operation = Map({ type: operationType, value });

  return productId
    ? operation.set('products', List([productId]))
    : operation;
}

export function makeInlineOperationValue({ channel, data, type }) {
  switch (channel) {
    case SHOPIFY: {
      const { value } = data;

      switch (type) {
        case ATTRIBUTE.BARCODE: {
          return Map({ shopify_barcode: value || '' });
        }

        case ATTRIBUTE.CAP: {
          return Map({ shopify_compare_at_price: value || AMOUNT.ZERO_CENT });
        }

        case ATTRIBUTE.CHARGE_TAX: {
          return Map({ shopify_taxable: value || false });
        }

        case ATTRIBUTE.CONTINUE_SELLING: {
          return Map({ shopify_inventory_policy: value ? 'continue' : 'deny' });
        }

        case ATTRIBUTE.COUNTRY_CODE: {
          return Map({ shopify_country_code_of_origin: value || null });
        }

        case ATTRIBUTE.CPI: {
          return Map({ shopify_cost: value || AMOUNT.ZERO_CENT });
        }

        case ATTRIBUTE.HS_CODE: {
          return Map({ shopify_harmonized_system_code: value || null });
        }

        case ATTRIBUTE.PHYSICAL: {
          return Map({ shopify_requires_shipping: value || false });
        }

        case ATTRIBUTE.PRICE: {
          return Map({ shopify_price: value || AMOUNT.ZERO_CENT });
        }

        case ATTRIBUTE.QUANTITY: {
          return Map({ shopify_inventory_quantity: value || AMOUNT.ZERO });
        }

        case ATTRIBUTE.SKU: {
          return Map({ shopify_sku: value || '' });
        }

        case ATTRIBUTE.TRACK_QUANTITY: {
          return Map({ shopify_inventory_management: value ? SHOPIFY : null });
        }

        case ATTRIBUTE.UNIT: {
          return Map({ shopify_weight_unit: value });
        }

        case ATTRIBUTE.WEIGHT: {
          return Map({ shopify_weight: value || AMOUNT.ZERO });
        }

        default: {
          return undefined;
        }
      }
    }

    default: {
      return undefined;
    }
  }
}

export function makeOperationValue({ channel, data, overset, product, type }) {
  if (!getOperationType({ channel, type })) {
    return undefined;
  }

  function inventoryLevelErrors() {
    function reduceInventoryForErrors(result, item, key) {
      return result || ((key in INVENTORY_OPERATIONS[channel]) && !!item.get(ERROR));
    }

    switch (channel) {
      case EBAY:
      case ETSY: {
        return !!product.getIn([FORM.INVENTORY, type, ERROR]);
      }

      case FAIRE: {
        switch (type) {
          case ATTRIBUTE.CONTINUE_SELLING:
          case ATTRIBUTE.KEEP_ACTIVE: {
            return false;
          }

          case ATTRIBUTE.RETAIL_PRICE:
          case ATTRIBUTE.WHOLESALE_PRICE: {
            return (
              !!product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, ERROR]) ||
              !!product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, ERROR])
            );
          }

          default: {
            return !!product.getIn([FORM.INVENTORY, type, ERROR]);
          }
        }
      }

      case SHOPIFY: {
        return product.get(FORM.INVENTORY).reduce(reduceInventoryForErrors, false);
      }

      default: {
        return false;
      }
    }
  }

  function variationsLevelErrors() {
    switch (channel) {
      case EBAY:
      case FAIRE:
      case SHOPIFY: {
        return reduceErrors(product.getIn([FORM.VARIATIONS, ERROR]));
      }

      case ETSY: {
        return !!product.getIn([FORM.INVENTORY, type, ERROR]);
      }

      default: {
        return false;
      }
    }
  }

  if (overset) {
    if (variationsLevelErrors()) return undefined;

    switch (channel) {
      case EBAY:
      case SHOPIFY:
      case FAIRE: {
        return shapeVariationsForAPI({ product, toImmutable: true }).get(VALUE);
      }

      case ETSY: {
        const { offeringId = null, combination = null, value } = data || {};
        if (
          (Map.isMap(combination) && offeringId === null) ||
          (isValidNumber(offeringId) && combination === null)
        ) {
          return getInventoryOperationValue({ channel, combination, offeringId, type, value });
        }

        return undefined;
      }

      default: {
        return undefined;
      }
    }
  } else {
    if (inventoryLevelErrors()) return undefined;

    switch (channel) {
      case EBAY:
      case ETSY: {
        return getInventoryOperationValue({ channel, inventory: product.get(FORM.INVENTORY), type });
      }

      case FAIRE: {
        switch (type) {
          case ATTRIBUTE.RETAIL_PRICE:
          case ATTRIBUTE.WHOLESALE_PRICE: {
            return getInventoryOperationValue({ channel, inventory: product.get(FORM.INVENTORY), type });
          }

          default: {
            const { value } = data;
            return getInventoryOperationValue({ channel, type, value });
          }
        }
      }

      case SHOPIFY: {
        return getInventoryOperationValue({ channel, inventory: product.get(FORM.INVENTORY) });
      }

      default: {
        return undefined;
      }
    }
  }
}

export function getInventoryFromVariations({
  asProfile,
  channel,
  imagesById,
  product: source,
  variations: data,
}) {
  let product = source;

  switch (channel) {
    case SHOPIFY: {
      if (
        getSize(get('options')(data)) > 0 &&
        getSize(get('variants')(data)) > 0 &&
        !(
          get(['options', 0, 'shopify_name'])(data) === 'Title' &&
          get(['variants', 0, 'shopify_option1'])(data) === 'Default Title'
        )
      ) {
        const firstOffering = get(['variants', 0])(data);
        const sku = get('shopify_sku')(firstOffering);
        const cpi = get('shopify_cost')(firstOffering);
        const price = get('shopify_price')(firstOffering);
        const weight = get('shopify_weight')(firstOffering);
        const barcode = get('shopify_barcode')(firstOffering);
        const unit = get('shopify_weight_unit')(firstOffering);
        const chargeTax = get('shopify_taxable')(firstOffering);
        const cap = get('shopify_compare_at_price')(firstOffering);
        const physical = get('shopify_requires_shipping')(firstOffering);
        const quantity = get('shopify_inventory_quantity')(firstOffering);
        const hsCode = get('shopify_harmonized_system_code')(firstOffering);
        const inventoryPolicy = get('shopify_inventory_policy')(firstOffering);
        const countryCode = get('shopify_country_code_of_origin')(firstOffering);
        const trackQuantity = get('shopify_inventory_management')(firstOffering) === SHOPIFY;
        const inventory = Map({
          [ATTRIBUTE.BARCODE]: makeValue(barcode || '').set(OVERSET, true),
          [ATTRIBUTE.CAP]: makeValue(toFixed(cap, 2) || '').set(OVERSET, true),
          [ATTRIBUTE.CHARGE_TAX]: makeValue(chargeTax),
          [ATTRIBUTE.COUNTRY_CODE]: makeValue(countryCode),
          [ATTRIBUTE.CONTINUE_SELLING]: makeValue(inventoryPolicy === 'continue'),
          [ATTRIBUTE.CPI]: makeValue(toFixed(cpi, 2) || '').set(OVERSET, true),
          [ATTRIBUTE.HS_CODE]: makeValue(hsCode),
          [ATTRIBUTE.PHYSICAL]: makeValue(physical).set(OVERSET, true),
          [ATTRIBUTE.PRICE]: makeValue(toFixed(price, 2) || '').set(OVERSET, true),
          [ATTRIBUTE.QUANTITY]: makeValue(toString(quantity) || '').set(OVERSET, true),
          [ATTRIBUTE.SKU]: makeValue(sku || '').set(OVERSET, true),
          [ATTRIBUTE.TRACK_QUANTITY]: makeValue(trackQuantity),
          [ATTRIBUTE.UNIT]: makeValue(unit || WEIGHT_UNITS[0]),
          [ATTRIBUTE.WEIGHT]: makeValue(weight || ''),
          [ATTRIBUTE.DEFAULT_VALUE]: Map({
            options: List([DEFAULT_OPTION[SHOPIFY]]),
            variants: List([
              Map({
                ...toJS(firstOffering),
                id: -1,
                deleted: false,
                shopify_position: 1,
                shopify_title: 'Default Title',
                shopify_option1: 'Default Title',
                shopify_option2: null,
                shopify_option3: null,
                shopify_id: 0,
                shopify_created_at: null,
                shopify_updated_at: null,
                shopify_inventory_item_id: undefined,
                shopify_admin_graphql_api_id: undefined,
                shopify_cost: toFixed(cpi, 2),
                shopify_price: toFixed(price, 2),
                shopify_compare_at_price: toFixed(cap, 2),
              }),
            ]),
          }),
        });

        product = product.set(FORM.INVENTORY, inventory);
        product = product.set(FORM.VARIATIONS, shapeVariationsForApp({ asProfile, data, imagesById, product }));
      } else {
        const productId = product.get('productId');
        const channelProductId = product.get('channelProductId');
        const firstOffering = get(['variants', 0])(data) || DEFAULT_VARIANT[SHOPIFY];
        const sku = get('shopify_sku')(firstOffering);
        const cpi = get('shopify_cost')(firstOffering);
        const price = get('shopify_price')(firstOffering);
        const weight = get('shopify_weight')(firstOffering);
        const barcode = get('shopify_barcode')(firstOffering);
        const unit = get('shopify_weight_unit')(firstOffering);
        const chargeTax = get('shopify_taxable')(firstOffering);
        const cap = get('shopify_compare_at_price')(firstOffering);
        const physical = get('shopify_requires_shipping')(firstOffering);
        const quantity = get('shopify_inventory_quantity')(firstOffering);
        const hsCode = get('shopify_harmonized_system_code')(firstOffering);
        const inventoryPolicy = get('shopify_inventory_policy')(firstOffering);
        const countryCode = get('shopify_country_code_of_origin')(firstOffering);
        const trackQuantity = get('shopify_inventory_management')(firstOffering) === SHOPIFY;
        const inventory = Map({
          [ATTRIBUTE.BARCODE]: makeValue(barcode || ''),
          [ATTRIBUTE.CAP]: makeValue(toFixed(cap, 2) || ''),
          [ATTRIBUTE.CHARGE_TAX]: makeValue(chargeTax),
          [ATTRIBUTE.COUNTRY_CODE]: makeValue(countryCode),
          [ATTRIBUTE.CONTINUE_SELLING]: makeValue(inventoryPolicy === 'continue'),
          [ATTRIBUTE.CPI]: makeValue(toFixed(cpi, 2) || ''),
          [ATTRIBUTE.HS_CODE]: makeValue(hsCode),
          [ATTRIBUTE.PHYSICAL]: makeValue(physical),
          [ATTRIBUTE.PRICE]: makeValue(toFixed(price, 2) || ''),
          [ATTRIBUTE.QUANTITY]: makeValue(toString(quantity) || ''),
          [ATTRIBUTE.SKU]: makeValue(sku || ''),
          [ATTRIBUTE.TRACK_QUANTITY]: makeValue(trackQuantity),
          [ATTRIBUTE.UNIT]: makeValue(unit || WEIGHT_UNITS[0]),
          [ATTRIBUTE.WEIGHT]: makeValue(weight || ''),
          [ATTRIBUTE.DEFAULT_VALUE]: Map({
            options: List([DEFAULT_OPTION[SHOPIFY]]),
            variants: List([
              Map({
                ...toJS(firstOffering),
                id: -1,
                deleted: false,
                shopify_position: 1,
                shopify_title: 'Default Title',
                shopify_option1: 'Default Title',
                shopify_option2: null,
                shopify_option3: null,
                shopify_id: 0,
                shopify_created_at: null,
                shopify_updated_at: null,
                shopify_inventory_item_id: undefined,
                shopify_admin_graphql_api_id: undefined,
                shopify_product_id: channelProductId,
                vela_product_id: productId,
                shopify_cost: toFixed(cpi, 2) || '',
                shopify_price: toFixed(price, 2) || '',
                shopify_compare_at_price: toFixed(cap, 2) || '',
              }),
            ]),
          }),
        });

        product = product.set(FORM.INVENTORY, inventory);
        product = product.set(FORM.VARIATIONS,
          shapeVariationsForApp({ asProfile, data: { options: [], variants: [] }, product })
        );
      }

      return product;
    }

    default: {
      return product;
    }
  }
}
